import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AdminLoginPage = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [backendStatus, setBackendStatus] = useState('checking');

  // Configure axios
  useEffect(() => {
    // Set base URL
    const apiUrl = 'https://api.marketmaestroovip.in';
    axios.defaults.baseURL = apiUrl;
    axios.defaults.timeout = 10000;
    
    // Test backend connection
    testBackendConnection();
    
    // Check if already logged in
    const token = localStorage.getItem('adminToken');
    if (token) {
      window.location.href = '/admin/';
    }
  }, []);

  const testBackendConnection = async () => {
    try {
      // Test health endpoint
      await axios.get('/health', { timeout: 5000 });
      setBackendStatus('connected');
      console.log('Backend connected successfully');
      
      // Also test if admin endpoint exists (but don't show error if it's a POST-only endpoint)
      try {
        await axios.head('/api/admin/login', { timeout: 3000 });
        console.log('Admin endpoint exists');
      } catch (headError) {
        // HEAD might fail for POST endpoints, that's okay
        console.log('HEAD request failed (normal for POST endpoints)');
      }
      
    } catch (error) {
      console.error('Connection test failed:', error);
      setBackendStatus('disconnected');
      setApiError(`Cannot connect to backend: ${error.message}`);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (apiError) setApiError('');
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setApiError('');
    setSuccessMessage('');
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    if (backendStatus === 'disconnected') {
      setApiError('Cannot connect to server. Please check backend connection.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Attempting admin login...');
      
      // Use the correct endpoint based on your backend
      const response = await axios.post('/api/admin/login', {
        username: formData.username,
        password: formData.password
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        // Remove withCredentials if you're using Authorization header instead of cookies
        // withCredentials: true
      });

      console.log('Login response:', response.data);

      if (response.data.success === true) {
        setSuccessMessage('Login successful! Redirecting...');
        
        // Store token from response
        const token = response.data.token;
        const adminData = response.data.admin;
        
        if (token) {
          localStorage.setItem('adminToken', token);
          localStorage.setItem('adminData', JSON.stringify(adminData));
          
          // Set Authorization header for future requests
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        }
        
        // If using cookies, the cookie should be set automatically by backend
        console.log('Login successful, cookie should be set');
        
        // Redirect to admin dashboard
        setTimeout(() => {
          window.location.href = '/admin/';
        }, 1500);
      } else {
        setApiError(response.data.message || 'Login failed');
        setFormData(prev => ({ ...prev, password: '' }));
      }
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      
      if (error.response) {
        if (error.response.status === 401) {
          setApiError('Invalid username or password');
        } else if (error.response.status === 404) {
          setApiError('Login endpoint not found (404). Check: /api/admin/login');
        } else if (error.response.status === 500) {
          setApiError('Server error. Please try again later.');
        } else {
          setApiError(`Error ${error.response.status}: ${error.response.data?.message || 'Login failed'}`);
        }
      } else if (error.code === 'ERR_NETWORK') {
        setApiError('Network error. Cannot connect to server.');
        setBackendStatus('disconnected');
      } else if (error.message.includes('timeout')) {
        setApiError('Request timeout. Server might be slow.');
      } else {
        setApiError('Login failed: ' + error.message);
      }
      
      setFormData(prev => ({ ...prev, password: '' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const retryConnection = () => {
    setApiError('');
    setBackendStatus('checking');
    testBackendConnection();
  };

  const testEndpointDirectly = async () => {
    try {
      // Open a simple test in new tab
      const response = await fetch('https://api.marketmaestroovip.in/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: 'test',
          password: 'test'
        })
      });
      
      const data = await response.json();
      alert(`Test response: ${JSON.stringify(data, null, 2)}`);
    } catch (error) {
      alert('Test failed: ' + error.message);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Connection Status */}
        {backendStatus === 'checking' && (
          <div className="mb-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
            <div className="flex items-center">
              <svg className="animate-spin h-5 w-5 text-blue-500 mr-3" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-blue-700">Testing connection to backend...</span>
            </div>
          </div>
        )}
        
        {backendStatus === 'disconnected' && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <div>
              <p className="text-red-700 font-medium">Connection Issue</p>
              <p className="text-sm text-red-600 mt-2">{apiError}</p>
              <button
                onClick={retryConnection}
                className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Retry Connection
              </button>
            </div>
          </div>
        )}

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Portal</h1>
              <p className="text-gray-600">Sign in to access the admin dashboard</p>
            </div>
            
            {/* Error/Success Messages */}
            {apiError && backendStatus === 'connected' && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                <p className="text-sm text-red-700">{apiError}</p>
              </div>
            )}
            
            {successMessage && (
              <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
                <p className="text-sm text-green-700">{successMessage}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              {/* Username Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    value={formData.username}
                    onChange={handleChange}
                    className={`pl-10 w-full px-4 py-3 border ${errors.username ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-lg focus:ring-2 focus:outline-none transition duration-200`}
                    placeholder="Enter admin username"
                    disabled={isSubmitting}
                    autoComplete="username"
                  />
                </div>
                {errors.username && (
                  <p className="mt-2 text-sm text-red-600">{errors.username}</p>
                )}
              </div>
              
              {/* Password Input */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`pl-10 w-full px-4 py-3 border ${errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'} rounded-lg focus:ring-2 focus:outline-none transition duration-200`}
                    placeholder="Enter password"
                    disabled={isSubmitting}
                    autoComplete="current-password"
                  />
                </div>
                {errors.password && (
                  <p className="mt-2 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting || backendStatus === 'disconnected'}
                className={`w-full py-3 px-4 rounded-lg font-medium text-white shadow-md transition-all duration-200 ${isSubmitting ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-lg'} focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>
            </form>
            
            {/* Debug : Info is the most importent for check server is runing or not */}
            {/* <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-xs font-medium text-gray-700 mb-2">Connection Details:</p>
                <div className="text-xs text-gray-600 space-y-1">
                  <p><strong>Backend:</strong> https://api.marketmaestroovip.in</p>
                  <p><strong>Status:</strong> 
                    <span className={`ml-2 px-2 py-1 rounded ${backendStatus === 'connected' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {backendStatus}
                    </span>
                  </p>
                  <p><strong>Login Endpoint:</strong> POST /api/admin/login</p>
                  <p><strong>Health Check:</strong> GET /health </p>
                </div>
                
                <button
                  onClick={testEndpointDirectly}
                  className="mt-3 text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Test endpoint directly
                </button>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;